// Base interfaces
export interface Empresa {
  id: string;
  nome: string;
  cnpj: string;
  razaoSocial: string;
  email: string;
  telefone: string;
  endereco: {
    rua: string;
    numero: string;
    complemento?: string;
    bairro: string;
    cidade: string;
    estado: string;
    cep: string;
  };
  status: 'ativo' | 'inativo';
  createdAt: Date;
  updatedAt: Date;
}

// Campo types for form fields
// Trecho de Rota de Viagem
export interface TrechoRota {
  id: string;
  origem: string;       // Cidade/Local de saída
  destino: string;      // Cidade/Local de chegada
  data: string;         // Data do trecho (ISO string ou dd/MM/yyyy)
  tipo: 'ida' | 'volta';
  observacao?: string;  // Observação opcional
}

export interface CampoConfig {
  id: string;
  nome: string;
  tipo: 'texto' | 'texto_longo' | 'numero' | 'data' | 'lista' | 'checkbox' | 'checkbox_single' | 'checkbox_multi' | 'progresso' | 'parcelas' | 'itens' | 'cotacoes' | 'valor' | 'anexo' | 'data_vencimento' | 'justificativa_checkbox' | 'telefone' | 'rotas' | 'rota_terrestre' | 'documento' | 'texto_informativo' | 'responsavel' | 'viajantes' | 'quadro_voos' | 'nd_vinculada';
  obrigatorio: boolean;
  opcoes?: string[];
  /** avisa quando o valor digitado nunca apareceu em nenhum card (fluxos de compras) */
  validarFornecedorNovo?: boolean;
  coresOpcoes?: Record<string, string>; // mapa valor-da-opcao -> cor hex para exibir como pill no mini card (tipos lista/checkbox*)
  valorMinimo?: number;
  valorMaximo?: number;
  precisao?: number;
  /** Campos de anexo: quantidade mínima de arquivos exigida (0/undefined = usa `obrigatorio`) */
  minAnexos?: number;
  /** Campos de anexo: quantidade máxima de arquivos aceita (default 6) */
  maxAnexos?: number;
  createdBy?: string; // ID do usuário que criou o campo
  integracaoId?: string; // ID da integração para campos de anexo
  campoVinculadoId?: string; // ID do campo checkbox vinculado (para justificativa_checkbox)
  usarComoTitulo?: boolean; // Se true, o valor deste campo será o título do cartão
  consumoMedioPadrao?: number; // km/l - para rota_terrestre (default: 10)
  precoCombustivelPadrao?: number; // R$/l - para rota_terrestre (default: 6.0)
  documentoTemplateId?: string; // ID do template de documento (para tipo 'documento')
  textoInformativo?: string; // Texto a exibir (para tipo 'texto_informativo')
  ocultarTitulo?: boolean; // Se true, oculta o título/nome do campo na exibição
  somenteAdmin?: boolean; // Se true, só administradores alteram o valor (os demais só veem)
  /** Campo interno: só quem participa de alguma etapa do fluxo (e os extras) vê.
   *  Também não vai para o acompanhamento do portal. */
  visivelSomenteParticipantes?: boolean;
  /** Pessoas de fora do fluxo que também veem o campo restrito (id ou e-mail) */
  visivelParaExtras?: string[];
  /** Só quem está em visivelParaExtras vê (participar do fluxo não basta).
   *  Administrador e admin master veem sempre. */
  visivelSomenteExtras?: boolean;
  formatacao?: { // Formatação do texto informativo
    negrito?: boolean;
    italico?: boolean;
    tamanho?: 'pequeno' | 'medio' | 'grande';
    cor?: string;
    alinhamento?: 'esquerda' | 'centro' | 'direita';
  };
}

// Configurações de Hierarquia de Cards
export interface ConfiguracaoHierarquiaEtapa {
  // Permitir cards pai nesta etapa
  permitirCardsPai: boolean;
  
  // Regras para transformar card em pai
  regrasTransformacao?: {
    // Quais condições devem ser atendidas para virar pai
    condicoes?: {
      campoId: string;
      operador: 'igual' | 'diferente' | 'contem' | 'nao_contem' | 'maior' | 'menor';
      valor: any;
    }[];
    // Automações que bloqueiam transformação em pai
    automacoesBloqueadoras?: string[];
    // Só pode virar pai se estiver em determinadas etapas
    etapasPermitidas?: string[];
  };
  
  // Regras para agrupar filhos
  regraAgrupamento?: {
    // Permite aceitar filhos
    aceitaFilhos: boolean;
    // Quais condições o filho deve ter para ser agrupado
    condicoesFilho?: {
      campoId: string;
      operador: 'igual' | 'diferente' | 'contem' | 'nao_contem' | 'maior' | 'menor';
      valor: any;
    }[];
    // Automações que bloqueiam card de virar filho (DESABILITADO - usar cardsExcecaoAutomacao)
    automacoesBloqueadorasFilho?: string[];
    // Cards específicos autorizados a virar filho mesmo com automações ativas
    cardsExcecaoAutomacao?: string[]; // Array de IDs de cards
    // Etapas que não permitem que cards virem filhos
    etapasProibidasParaFilhos?: string[];
    // Limite máximo de filhos por pai
    limiteFilhos?: number;
  };
  
  // Visual do card pai
  visualCardPai?: {
    corDestaque?: string;
    icone?: string;
    mostrarContagemFilhos?: boolean;
  };
}

// Kanban and Flow related interfaces
export interface Etapa {
  id: string;
  nome: string;
  campos: CampoConfig[];
  departamentoId?: string;
  participantes?: string[];
  permiteMoverPara?: string[];
  permiteCriarCards?: boolean;
  empresaId: string;
  // Ordem/posição da etapa no fluxo BPMN (0 = mais à esquerda)
  ordemFluxo?: number;
  // Quais campos (por id) devem ser exibidos no preview do card na coluna
  // Se vazio/undefined, exibe todos (comportamento atual)
  camposVisiveisNoCard?: string[];
  // Campos que devem aparecer SEMPRE no card, independente da etapa
  camposTravados?: string[];
  // IDs dos templates de documentos habilitados para esta etapa
  documentosHabilitados?: string[];
  // Configuração de hierarquia de cards (sistema pai/filho)
  configuracaoHierarquia?: ConfiguracaoHierarquiaEtapa;
  // 💰 Campos que a cotação leva por padrão quando sai desta etapa.
  // Sem isto, quem pede orçamento remarca a mesma lista toda vez.
  cotacaoCampos?: CamposCotacaoEtapa;
  // 💰 Fornecedores habituais desta etapa: o pedido já abre com eles preenchidos.
  cotacaoFornecedores?: FornecedorCotacao[];
  // Configuração de notificações por e-mail
  notificacoesEmail?: {
    habilitado: boolean;
    // Configuração individual por colaborador
    colaboradores?: {
      [colaboradorId: string]: {
        receberNotificacao: boolean;
      };
    };
  };
  // Configuração de notificações por WhatsApp
  notificacoesWhatsapp?: {
    habilitado: boolean;
    // Configuração individual por colaborador
    colaboradores?: {
      [colaboradorId: string]: {
        receberNotificacao: boolean;
      };
    };
  };
  // Configuração de preenchimento de campos via WhatsApp
  camposWhatsapp?: {
    habilitado: boolean;
    // IDs dos campos que serão perguntados via WhatsApp
    campos: string[];
    // Colaboradores que receberão as perguntas
    colaboradores?: {
      [colaboradorId: string]: {
        receberNotificacao: boolean;
      };
    };
  };
  // 📎 Etapa de reprovação que devolve anexos para o solicitante do portal.
  // Ao mover um card para cá, abre o modal pedindo QUAIS campos de anexo o
  // solicitante precisa reenviar. Ele vê só esses campos na tela de acompanhamento.
  solicitarReenvioAnexos?: boolean;
  // Etapa para onde o card volta sozinho quando o solicitante reenvia os arquivos.
  // Vazio = volta para a etapa de onde o card saiu.
  etapaRetornoReenvioId?: string;
  // Etiqueta aplicada no card quando ele volta por reenvio (ex.: "Reanálise"),
  // para a equipe distinguir o que voltou do que nunca saiu. Vazio = sem etiqueta.
  etiquetaReenvioNome?: string;
  etiquetaReenvioCor?: string;
  // Campo de motivo interno: card reprovado só por ele (sem motivo de documento)
  // entra aqui direto, sem o modal de reenvio e sem voltar para reanálise.
  motivoInternoCampoId?: string;
  // Configuração de ordenação dos cards na etapa
  ordenacaoCards?: 'mais_recente' | 'mais_antigo' | 'vencimento_proximo' | 'vencimento_distante' | 'titulo_az' | 'titulo_za';
  // Responsável padrão para novos cards criados nesta etapa
  responsavelPadraoId?: string;
  // Quem mover o card para esta etapa (arrastando ou "Mover para") vira o responsável.
  // Tem prioridade sobre regras condicionais e responsável padrão; movimento feito
  // por automação não conta — aí valem as regras normais.
  responsavelQuemMoveu?: boolean;
  // Campos de outras etapas que devem ser exibidos nesta etapa (formato: "etapaId:campoId")
  camposOutrasEtapas?: string[];
  // Regras condicionais para atribuição automática de responsável
  regrasResponsavel?: {
    tipo: 'criador' | 'responsavel_anterior' | 'campo';
    condicoes: Array<{
      quando: string; // ID do colaborador ou valor do campo
      atribuirPara: string; // ID do colaborador que será atribuído
    }>;
  };
  // Tempo máximo esperado (SLA) em horas para cards ficarem nesta etapa (opcional)
  tempoMaximoHoras?: number;
  // Se true, sábados e domingos não contam no cálculo do SLA
  slaIgnorarFimDeSemana?: boolean;
  // Alerta WhatsApp ao responsável quando SLA do card atrasar
  alertaSlaWhatsapp?: {
    habilitado: boolean;
  };
  // Escalonamento por e-mail quando o card passa de 2x, 3x, 4x... o SLA da etapa.
  // Diferente do alertaSlaWhatsapp (que avisa uma vez, ao estourar): aqui cada
  // múltiplo do prazo dispara um aviso novo, subindo um degrau do organograma.
  escalonamentoSla?: {
    habilitado: boolean;
    // Recebem em TODOS os níveis, independente da hierarquia
    destinatariosExtras?: string[];  // ids de colaboradores
    emailsExtras?: string[];         // e-mails avulsos (ex.: alias de diretoria)
  };
  // Se false, o campo Título padrão do card não aparece (default: true para retrocompatibilidade)
  mostrarCampoTitulo?: boolean;
  // Configuração de limpeza de campos ao card retornar para esta etapa
  limparCamposAoRetornar?: {
    ativo: boolean;
    campos: string[]; // IDs dos campos que devem ser limpos
  };
  // Cards personalizados (modelos com campos pré-preenchidos) desta etapa.
  // Ao criar um card nesta etapa, o usuário pode escolher um modelo para auto-preencher os campos.
  cardsPersonalizados?: CardTemplate[];
  // Integração GIX (somente ambiente local): ID do campo que deve exibir o badge de status da OC.
  // Quando configurado, ao abrir um card dessa etapa o sistema consulta /api/gix/oc/{valor} e mostra um badge
  // clicável abaixo do campo; o clique abre modal com detalhes da OC.
  gixBadgeCampoId?: string;
  // Integração GIX (somente ambiente local): ID do campo acima do qual aparece o botão "Criar OC no GIX".
  // O botão abre o formulário GixCriarOcForm com pré-preenchimento a partir dos campos do card.
  // Quando a OC é criada, o número retornado é gravado neste mesmo campo.
  gixCriarOcCampoId?: string;
  // Integração GIX: ID do campo (tipo lista Sim/Não) que, ao ser respondido com "Sim" via WhatsApp,
  // dispara a liberação automática da OC no GIX (mesma ação do botão 🔓 Liberar OC).
  // O número da OC é lido do campo configurado em gixBadgeCampoId.
  gixAprovacaoCampoId?: string;
  // Integração GIX (somente ambiente local): quando true, esta etapa varre os cards
  // e aprova automaticamente no GIX as OCs cujo tipo de movimento é 96 (ex.: E96).
  // O número da OC é lido do campo configurado em gixBadgeCampoId. A aprovação real
  // só ocorre se GIX_OC_AUTO_APROVAR=1 no api_gix.py (senão roda em dry-run).
  gixAutoAprovarMov96?: boolean;
  // Integração GIX: quando true, esta etapa tenta LANÇAR a nota fiscal no GIX
  // automaticamente (entrada de mercadoria), apenas para OC de movimento E96.
  // O lançamento real só acontece com GIX_NF_ALLOW_INSERT=1 na API GIX; sem isso
  // roda em dry-run e o card não sai do lugar.
  gixLancarNfMov96?: boolean;
  // Etapa para onde o card vai quando a nota é lançada com sucesso.
  // Sem isso, usa a etapa seguinte do fluxo.
  gixLancarNfEtapaDestino?: string;
  // Etapa para onde o card vai quando o lançamento NÃO acontece — a coluna
  // "Notas não lançadas". O motivo fica gravado no campo abaixo e no histórico.
  gixLancarNfEtapaFalha?: string;
  /** Etapa para nota de serviço (lançamento manual). Vazio = não move. */
  gixLancarNfEtapaServico?: string;
  // Campo de texto onde o motivo da recusa é escrito (ex.: "Divergências encontradas").
  gixLancarNfCampoMotivo?: string;
  // Configuração de fatiamento de card (🔪 "Fatiar card")
  configuracaoFatiamento?: ConfiguracaoFatiamentoEtapa;
}

// ========== COTAÇÃO COM FORNECEDOR ==========
// Manda um card que já existe para alguém de fora cotar. Fica no fluxo, e não
// na etapa: ou o fluxo trabalha com orçamento de terceiros, ou não trabalha.
// Modelo de campos da etapa: o que o fornecedor lê e o que ele preenche.
// Fica na etapa (e não no fluxo) porque é ali que se configura, junto do
// fatiamento — e é a etapa que define de onde a cotação sai.
// Fornecedor que a etapa costuma cotar. Fica salvo para não redigitar nome,
// e-mail e telefone a cada pedido — quem pede ainda pode mexer na hora.
export interface FornecedorCotacao {
  nome: string;
  email?: string;
  telefone?: string;
}

export interface CamposCotacaoEtapa {
  // Contexto (só leitura). Pode vir de QUALQUER etapa do fluxo.
  contexto: string[];
  // O que ele preenche. Só campos da própria etapa: a resposta nasce como card
  // filho nela, e campo de outra etapa não apareceria em tela nenhuma.
  resposta: string[];
}

export interface ConfiguracaoCotacao {
  habilitado: boolean;
  // Texto do botão no card. Padrão: "Pedir cotação"
  rotulo?: string;
}

// ========== FATIAMENTO DE CARDS ==========
// Permite quebrar um card em cards novos, escolhendo na hora quais campos vão junto.
// Diferente de Cards Conectados (onde o admin fixa o mapeamento de campos na config),
// aqui quem escolhe os campos é o usuário, card a card.
export interface ConfiguracaoFatiamentoEtapa {
  habilitado: boolean;
  // Texto do botão no card. Padrão: "Fatiar card"
  rotulo?: string;
  // Campos que o usuário pode marcar para levar na fatia.
  // Vazio/undefined = todos os campos da etapa.
  camposFatiaveis?: string[];
  // Campos que vão SEMPRE na fatia, sem opção de desmarcar (ex.: CNPJ, obra, solicitante).
  // Nunca são removidos do card de origem.
  camposSempreJuntos?: string[];
  // Etapa onde a fatia nasce. Vazio = mesma etapa do card de origem.
  etapaDestinoId?: string;
  // Prefixo do título da fatia. Suporta {n} (número da fatia) e {titulo} (título da origem).
  // Padrão: "[FATIA {n}]"
  prefixoTitulo?: string;
  // Levar os anexos dos campos marcados para a fatia
  copiarAnexos?: boolean;
  // Se true, os campos marcados CONTINUAM no card de origem (fatia = cópia).
  // Se false (padrão), saem do card de origem — o valor fica preservado no
  // snapshot do etapaHistory, então nada se perde historicamente.
  origemMantemCampos?: boolean;
}

// Configuração de recorrência (criação automática) de um Card Personalizado.
// Quando ativa, o servidor cria automaticamente um card a partir deste modelo
// no horário/frequência configurados (mesmo motor das automações agendadas).
export interface CardTemplateRecorrencia {
  ativo: boolean;
  frequencia: 'diario' | 'semanal' | 'mensal';
  hora: number;   // 0-23 (horário de Brasília)
  minuto: number; // 0-59
  diaDaSemana?: number; // 0=domingo .. 6=sábado (usado quando frequencia = 'semanal')
  diaDoMes?: number;    // 1-31 (usado quando frequencia = 'mensal')
  ultimaExecucao?: string; // marcador interno do servidor "YYYY-MM-DD HH:MM" (não editar manualmente)
}

// Modelo de card pré-configurado, escopado por etapa.
// 'valores' mapeia o id do campo para o valor padrão a ser aplicado.
export interface CardTemplate {
  id: string;
  nome: string;
  descricao?: string;
  cor?: string;
  titulo?: string; // Título padrão do card (quando a etapa usa o campo título padrão)
  valores: Record<string, any>;
  criadoEm?: string;
  criadoPor?: string;
  // Recorrência opcional: cria o card automaticamente conforme agendamento.
  recorrencia?: CardTemplateRecorrencia;
}

// 📎 Pedido de reenvio de anexo feito pela equipe interna ao solicitante do portal.
export interface SolicitacaoReenvioCampo {
  campoId: string;
  nome: string;
  motivo?: string;
}

export interface SolicitacaoReenvio {
  id: string;
  status: 'pendente' | 'atendida';
  campos: SolicitacaoReenvioCampo[];
  observacao?: string;
  solicitadoEm: string;
  solicitadoPor?: string;
  solicitadoPorNome?: string;
  // Etapa em que o card estava antes de ser reprovado
  etapaOrigemId?: string;
  // Etapa para onde o card volta quando o solicitante reenviar
  etapaRetornoId?: string;
  atendidoEm?: string;
}

export interface Card {
  id: string;
  etapaId: string;
  titulo: string;
  campos: Record<string, any>;
  responsavelId?: string;
  participantesIds?: string[];
  createdAt: Date;
  updatedAt: Date;
  empresaId: string;
  criadoPor?: string; // ID do usuário que criou o card

  // ========== ETIQUETAS ==========
  // IDs das etiquetas (definidas no nível do fluxo) vinculadas a este card.
  etiquetasIds?: string[];
  
  // ========== RESPONSÁVEL INDIVIDUAL (Opcional) ==========
  // ID do colaborador responsável específico por este card (diferente dos responsáveis da etapa)
  responsavelIndividualId?: string;
  
  // ========== HIERARQUIA DE CARDS (Sistema Pai/Filho) ==========
  // Indica se este card é um card pai
  ehCardPai?: boolean;
  // ID do card pai (se este for um filho)
  cardPaiId?: string;
  // IDs dos cards filhos (se este for um pai)
  cardsFilhosIds?: string[];
  // Referência aos objetos dos cards filhos (populado no frontend)
  cardsFilhos?: Card[];
  // Valor total do card pai (soma dos valores médios dos filhos + pai), editável
  valorTotal?: number;
  
  // Histórico real de etapas percorridas
  etapaHistory?: {
    etapaId: string;
    movedAt: Date;
    movedBy?: string;
    motivo?: string; // 'criado' | 'dragdrop' | 'edicao' | 'automacao' | etc
    automacaoId?: string;
    automacaoNome?: string;
    camposSnapshot?: Record<string, any>; // Snapshot dos valores dos campos no momento da movimentação
    leftAt?: Date; // Data que saiu desta etapa (para calcular tempo de permanência)
    responsavelId?: string; // ID do responsável nesta etapa
  }[];
  // 📎 Pedido de reenvio de anexo pendente/atendido (fluxo de reprovação do portal)
  solicitacaoReenvio?: SolicitacaoReenvio;
  // Campos de auditoria
  lastEditedBy?: string; // ID do usuário que fez a última edição
  lastEditedAt?: Date;   // Data da última edição
  fieldHistory?: {       // Histórico de alterações dos campos
    [fieldId: string]: {
      value: any;
      oldValue?: any;
      editedBy: string;
      editedAt: Date;
    }[];
  };
  // Histórico de eventos de hierarquia (virou pai, destravou pai, etc.)
  hierarchyHistory?: {
    event: 'tornou_pai' | 'destravou_pai' | 'adicionou_filho' | 'removeu_filho';
    at: Date | string;
    by?: string;
    detalhes?: string; // ex: nome do card filho adicionado/removido
  }[];

  // ========== CARDS CONECTADOS (Subprocessos) ==========
  // Indica se este card foi gerado como card conectado de outro
  isCardConectado?: boolean;
  // ID do card de origem que gerou este card conectado
  cardConectadoOrigemId?: string;
  // Flow ID de origem do card conectado
  cardConectadoOrigemFlowId?: string;
  // IDs de cards conectados gerados a partir deste card
  cardsConectadosIds?: string[];
  // Cor de destaque para cards conectados (definida na configuração)
  corCardConectado?: string;

  // ========== FATIAMENTO ==========
  // Preenchido na fatia: de onde ela veio. O card de origem pode até mudar de
  // etapa ou de conteúdo depois; estes dados são um retrato do momento do corte.
  fatiadoDe?: {
    cardId: string;
    flowId: string;
    etapaId: string;
    titulo: string;
    fatiadoEm: Date | string;
    fatiadoPor?: string;
    camposLevados: string[]; // ids dos campos que vieram na fatia
  };
  // Preenchido no card de origem: ids das fatias já geradas a partir dele.
  fatiasIds?: string[];
}

// ========== CARDS CONECTADOS ==========
export interface ConfiguracaoCardConectado {
  id: string;
  nome: string;
  descricao?: string;
  // Tipo da configuração
  tipo?: 'criar' | 'mover_existente'; // 'criar' = cria novo card, 'mover_existente' = move card existente conectado
  // Origem (gatilho)
  fluxoOrigemId: string;
  etapaOrigemId: string; // Etapa que habilita a criação/movimentação de card conectado
  // Destino
  fluxoDestinoId: string;
  etapaDestinoId: string; // Etapa onde o novo card será criado ou para onde o existente será movido
  // Mapeamento de campos (origem -> destino)
  mapeamentoCampos?: {
    campoOrigemId: string;
    campoDestinoId: string;
  }[];
  // Copiar histórico/timeline do card original
  copiarHistorico: boolean;
  // Copiar anexos do card original
  copiarAnexos: boolean;
  // Cor visual para o card conectado no kanban
  corDestaque: string;
  // Se o card conectado deve receber título automático
  prefixoTitulo?: string; // Ex: "[SUB]" ou "[CONECTADO]"
  // Obrigar preenchimento dos campos ao criar o card conectado
  obrigarPreenchimento?: boolean;
  // Condições para filtrar quando o card conectado deve ser criado
  condicoes?: {
    campo: string; // 'responsavel' | 'criador' | campoId
    operador: 'igual' | 'diferente' | 'contem' | 'nao_contem' | 'vazio' | 'nao_vazio';
    valor?: string;
  }[];
  condicoesLogica?: 'E' | 'OU'; // AND/OR entre condições (padrão: E)
  // Status
  ativo: boolean;
  empresaId: string;
  criadoPor?: string;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface CardConectadoRelacao {
  id: string;
  // Card de origem
  cardOrigemId: string;
  cardOrigemTitulo: string;
  flowOrigemId: string;
  flowOrigemNome: string;
  etapaOrigemId: string;
  etapaOrigemNome: string;
  // Card de destino (conectado)
  cardDestinoId: string;
  cardDestinoTitulo: string;
  flowDestinoId: string;
  flowDestinoNome: string;
  etapaDestinoId: string;
  etapaDestinoNome: string;
  // Metadados
  configuracaoId: string;
  criadoPor?: string;
  createdAt: Date;
  empresaId: string;
}

export interface Flow {
  id: string;
  name: string;
  descricao?: string;
  departamentoId: string;
  responsavelId: string;
  participantes: string[];
  etapas: Etapa[];
  cards: Card[];
  createdAt: Date;
  updatedAt: Date;
  status: 'ativo' | 'inativo';
  empresaId: string;
  createdBy?: string; // ID do usuário que criou o fluxo
  criadoPorNome?: string; // Nome do usuário que criou o fluxo
  parentFlowId?: string; // ID do fluxo pai (agrupamento visual apenas, 1 nível)
  // Biblioteca de etiquetas compartilhada por todos os cards do fluxo.
  etiquetas?: Etiqueta[];
  // Nem todo fluxo pede orçamento para fora, então isto vive no fluxo inteiro.
  configuracaoCotacao?: ConfiguracaoCotacao;
  // ⏱️ Prazo do processo: um relógio só para o card inteiro, desde a criação,
  // em vez de um prazo por etapa. Etapas de pausa congelam o ciclo; quando o
  // card sai delas (ex.: reenvio → Análise) começa um ciclo novo com o prazo cheio.
  prazoProcesso?: PrazoProcesso;
}

export interface PrazoProcesso {
  habilitado: boolean;
  horas: number;
  ignorarFimDeSemana?: boolean;
  // Vazio = as etapas de reprovação com reenvio (solicitarReenvioAnexos)
  etapasPausaIds?: string[];
}

// Etiqueta (tag/label) configurada no nível do fluxo e vinculada aos cards
// para facilitar visualização, filtro e relatórios.
export interface Etiqueta {
  id: string;
  nome: string;
  cor: string; // cor hex (ex.: '#6366F1')
}

export interface Departamento {
  id: string;
  nome: string;
  descricao: string;
  responsavel: string;
  createdAt: Date;
  updatedAt: Date;
  empresaId: string;
}

export interface Colaborador {
  id: string;
  userId?: string; // ID do usuário vinculado (User._id)
  nome: string;
  email: string;
  telefone?: string;
  cargo: string;
  departamentoId: string;
  superiorId?: string;
  dataContratacao: Date;
  status: 'ativo' | 'inativo';
  isGestor: boolean;
  foto?: string; // URL da foto do colaborador
  createdAt: Date;
  updatedAt: Date;
  fluxosResponsavel?: string[];
  fluxosParticipante?: string[];
  empresaId: string;
}

// Rich Editor interfaces
export interface EditorContent {
  type: string;
  content: string;
  format?: {
    bold?: boolean;
    italic?: boolean;
    heading?: 1 | 2;
    list?: 'bullet' | 'ordered';
  };
}

export interface ProcedimentoRichContent {
  sections: {
    title: string;
    content: EditorContent[];
  }[];
  metadata?: {
    lastEdit: Date;
    version: string;
    editedBy: string;
  };
}

// Process and BPM interfaces
export type ProcedimentoStatus = 'rascunho' | 'revisao' | 'publicado';

export interface Pool {
  id: string;
  name: string;
  lanes: Lane[];
  resources: Resource[];
  empresaId: string;
}

export interface Lane {
  id: string;
  name: string;
  poolId: string;
  resourceIds: string[];
  empresaId: string;
}

export interface Resource {
  id: string;
  name: string;
  type: 'human' | 'system' | 'document';
  poolId: string;
  empresaId: string;
}

export interface ProcessElement {
  id: string;
  type: 'start' | 'end' | 'task' | 'decision' | 'database' | 'arrow' | 
        'timer' | 'message' | 'error' | 'subprocess' | 'parallel' | 'exclusive' |
        'user_task' | 'service_task' | 'script_task' | 'manual_task' |
        'intermediate_event' | 'boundary_event' | 'annotation';
  label: string;
  x: number;
  y: number;
  width: number;
  height: number;
  poolId: string;
  laneId?: string;
  description?: string;
  responsible?: string;
  condition?: string;
  systemRef?: string;
  duration?: number;
  empresaId: string;
}

export interface DiagramaProcesso {
  elements: ProcessElement[];
  pools: Pool[];
}

export interface Anexo {
  id: string;
  nome: string;
  tipo: string;
  url: string;
  adicionadoEm: Date;
  adicionadoPor: string;
  empresaId: string;
}

export interface RevisaoHistorico {
  versao: string;
  data: Date;
  autor: string;
  alteracoes: string[];
  empresaId: string;
}

export interface Capitulo {
  id: string;
  titulo: string;
  conteudo: string;
  subcapitulos: Subcapitulo[];
}

export interface Subcapitulo {
  id: string;
  titulo: string;
  conteudo: string;
}

export interface ProcedimentoDescricao {
  capitulos: Capitulo[];
}

// Main Procedimento interface
export interface Procedimento {
  id: string;
  titulo: string;
  descricao: ProcedimentoDescricao;
  conteudoRico?: ProcedimentoRichContent;
  departamentoId: string;
  participantes: string[];
  versao: string;
  diagramaProcesso: DiagramaProcesso | null;
  status: ProcedimentoStatus;
  createdAt: Date;
  updatedAt: Date;
  criadoPor?: string;
  aprovadoPor?: string;
  anexos?: Anexo[];
  historico?: RevisaoHistorico[];
  observacoes: string;
  dataVencimento: Date | null;
  dataCriacao: Date;
  missaoProcesso: string;
  atores: string[];
  recursos: string[];
  problemas: string[];
  ruptura: string;
  dores: string[];
  riscos: string[];
  indicadores: string[];
  empresaId: string;
}

// Nova interface para Pastas de Procedimentos
export interface PastaProcedimento {
  id: string;
  nome: string;
  descricao?: string;
  cor?: string;
  icone?: string;
  createdAt: Date;
  updatedAt: Date;
  empresaId: string;
}

// Nova interface para Procedimento Simples (versão simplificada)
export interface ProcedimentoSimples {
  id: string;
  nome: string;
  objetivo: string;
  arquivo?: {
    nome: string;
    tipo: string;
    tamanho: number;
    dataUrl?: string;  // Legado (base64) - procedimentos antigos
    url?: string;      // Novo: URL local do servidor (/api/procedimentos-simples/arquivos/...)
    uploadedAt: Date;
  };
  pastaId: string;
  colaboradores?: string[]; // IDs dos colaboradores vinculados
  createdAt: Date;
  updatedAt: Date;
  empresaId: string;
  criadoPor: string;
}

// Template interfaces
export interface POPTemplate {
  id: string;
  name: string;
  description: string;
  content: string;
  sections: {
    title: string;
    content: string;
  }[];
  empresaId: string;
}

// Component Props interfaces
export interface EmpresasProps {
  initialEmpresas: Empresa[];
  onEmpresasChange: (empresas: Empresa[]) => void;
  onSelectEmpresa: (empresa: Empresa) => void;
}

export interface ColaboradorModalProps {
  colaborador: Colaborador | null;
  departamentos: Departamento[];
  colaboradores: Colaborador[];
  onSave: (colaborador: Omit<Colaborador, 'id' | 'createdAt' | 'updatedAt'> | Colaborador) => void;
  onClose: () => void;
}

export interface ProcessoEditorProps {
  procedimento: {
    id: string;
    diagramaProcesso: DiagramaProcesso | null;
  };
  onSave: (diagrama: DiagramaProcesso) => void;
  onClose: () => void;
}

export interface ProcedimentosProps {
  departamentos: Departamento[];
  colaboradores: Colaborador[];
  initialProcedimentos: Procedimento[];
  onProcedimentosChange: (procedimentos: Procedimento[]) => void;
}

export interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  className?: string;
}

export interface EditorToolbarProps {
  editor: any;
  templates?: POPTemplate[];
  onTemplateSelect?: (templateId: string) => void;
}

export interface TemplateSelectorProps {
  templates: POPTemplate[];
  onSelect: (template: POPTemplate) => void;
  onClose: () => void;
}

// Utility types
export type ElementType = 'start' | 'end' | 'task' | 'decision' | 'database' | 'arrow';

export interface Position {
  x: number;
  y: number;
}

import type { ComponentType } from 'react';

export interface MenuItem {
  id: string;
  name: string;
  icon: ComponentType<React.SVGProps<SVGSVGElement>>;
  subItems?: {
    id: string;
    name: string;
    icon: ComponentType<React.SVGProps<SVGSVGElement>>;
    description?: string;
  }[];
}

export interface Automacao {
  id: string;
  nome: string; // Nome da automação para facilitar identificação
  flowId: string;
  etapaOrigemId: string;
  etapaDestinoId?: string; // Opcional agora, pois nem toda ação precisa de destino
  condicoes: Array<{
    campoId: string;
    tipo: 'preenchido' | 'valor_igual' | 'valor_maior' | 'valor_menor';
    valor?: string | number;
  }>;
  acoes: Array<{
    tipo: 'mover_card' | 'enviar_email' | 'enviar_whatsapp' | 'exibir_ocultar' | 'atribuir_responsavel' | 'criar_card_conectado' | 'mover_card_conectado' | 'atribuir_api' | 'criar_card_template' | 'atualizar_campo';
    configuracao: {
      // Para mover_card
      etapaDestinoId?: string;
      // Regras opcionais para mover card (SE/ENTÃO/SENÃO)
      regrasMover?: Array<{
        condicao: {
          campoId: string;
          operador: 'igual' | 'diferente' | 'contem' | 'nao_contem' | 'maior' | 'menor' | 'maior_igual' | 'preenchido' | 'vazio' | 'dias_restantes';
          valor?: string | number;
        };
        acaoEntao: {
          tipo: 'mover' | 'nao_mover';
          etapaDestinoId?: string; // requerido quando tipo === 'mover'
        };
        acaoSenao?: {
          tipo: 'mover' | 'nao_mover';
          etapaDestinoId?: string;
        };
      }>;
      // Para enviar_email
      templateId?: string;
      destinatarios?: string[];
      cc?: string[];
      bcc?: string[];
      // Regras opcionais para envio condicional de e-mail (SE/ENTÃO/SENÃO)
      regrasEmail?: Array<{
        condicao: {
          campoId: string;
          operador: 'igual' | 'diferente' | 'contem' | 'nao_contem' | 'maior' | 'menor' | 'maior_igual' | 'preenchido' | 'vazio' | 'dias_restantes';
          valor?: string | number;
        };
        acaoEntao: {
          tipo: 'enviar' | 'nao_enviar';
          templateId?: string; // requerido quando tipo === 'enviar'
          destinatarios?: string[];
          cc?: string[];
          bcc?: string[];
        };
        acaoSenao?: {
          tipo: 'enviar' | 'nao_enviar';
          templateId?: string;
          destinatarios?: string[];
          cc?: string[];
          bcc?: string[];
        };
      }>;
      // Para enviar_whatsapp
      regrasWhatsapp?: Array<{
        condicao: {
          campoId: string;
          operador: 'igual' | 'diferente' | 'contem' | 'nao_contem' | 'maior' | 'menor' | 'maior_igual' | 'preenchido' | 'vazio' | 'dias_restantes';
          valor?: string | number;
        };
        acaoEntao: {
          tipo: 'enviar' | 'nao_enviar';
          templateId?: string; // requerido quando tipo === 'enviar'
          destinatarios?: string[];
        };
        acaoSenao?: {
          tipo: 'enviar' | 'nao_enviar';
          templateId?: string;
          destinatarios?: string[];
        };
      }>;
      // Para exibir_ocultar
      regras?: Array<{
        condicao: {
          campoId: string;
          operador: 'igual' | 'diferente' | 'contem' | 'nao_contem' | 'maior' | 'menor' | 'maior_igual' | 'preenchido' | 'vazio' | 'dias_restantes';
          valor?: string | number;
        };
        acao: 'mostrar' | 'esconder';
        elementosAfetados: Array<{
          tipo: 'campo' | 'secao' | 'botao' | 'texto';
          id: string;
          nome: string;
          opcoesEspecificas?: string[]; // Para campos com múltiplas opções, especificar quais opções afetar
        }>;
        // Campos opcionais para ENTÃO e SENÃO
        temEntao?: boolean;
        temSenao?: boolean;
        acaoSenao?: 'mostrar' | 'esconder';
        elementosAfetadosSenao?: Array<{
          tipo: 'campo' | 'secao' | 'botao' | 'texto';
          id: string;
          nome: string;
          opcoesEspecificas?: string[]; // Para campos com múltiplas opções, especificar quais opções afetar
        }>;
      }>;
      // Para criar_card_conectado
      configuracaoCardConectadoId?: string; // ID da configuração de card conectado a usar
      regrasCardConectado?: Array<{
        condicao: {
          campoId: string;
          operador: 'igual' | 'diferente' | 'contem' | 'nao_contem' | 'maior' | 'menor' | 'maior_igual' | 'preenchido' | 'vazio' | 'dias_restantes';
          valor?: string | number;
        };
        acaoEntao: {
          tipo: 'criar' | 'nao_criar';
          configuracaoCardConectadoId?: string;
        };
        acaoSenao?: {
          tipo: 'criar' | 'nao_criar';
          configuracaoCardConectadoId?: string;
        };
      }>;
      // Para mover_card_conectado
      moverCardConectado?: {
        fluxoDestinoConectadoId?: string; // Fluxo para onde mover o card conectado
        etapaDestinoConectadoId?: string; // Etapa destino do card conectado
        fluxoDestinoAtualId?: string; // Fluxo destino do card atual (opcional)
        etapaDestinoAtualId?: string; // Etapa destino do card atual (opcional)
        copiarDadosConexao?: boolean; // Copiar dados da conexão para o card movido
        direcao?: 'origem' | 'destino'; // Buscar card conectado como origem ou destino
      };
      // Para criar_card_template (automação agendada cria um card a partir de um CardTemplate)
      criarCardTemplate?: {
        fluxoDestinoId: string;          // fluxo onde o card será criado
        etapaDestinoId: string;          // etapa onde o card será criado
        templateId: string;              // id do CardTemplate dentro de etapa.cardsPersonalizados
        responsavelId?: string;          // colaborador responsável (opcional — pode vir do template)
        tituloCustom?: string;           // título customizado (opcional — usa o do template se vazio)
        camposExtras?: Array<{           // valores adicionais para campos da etapa (sobrescreve o template)
          campoId: string;
          valor: string;                 // aceita placeholders: {{hoje}}, {{hoje+30d}}, {{hoje+1m}}, {{dia}}, {{mes}}, {{ano}}
        }>;
      };
      // Para atualizar_campo: grava valores fixos em campos do card quando ele
      // entra na etapa (ex.: cair em "Concluídos" marca Status = Concluído e
      // Aguardando nossa resposta = Não).
      campos?: Array<{
        campoId: string;
        valor: string;
      }>;
      // Para atribuir_api
      regrasApi?: Array<{
        condicao: {
          campoId: string;
          operador: 'igual' | 'diferente' | 'contem' | 'nao_contem' | 'maior' | 'menor' | 'maior_igual' | 'preenchido' | 'vazio' | 'dias_restantes';
          valor?: string | number;
        };
        acaoEntao: {
          tipo: 'chamar_api' | 'nao_chamar';
          url: string;
          metodo: 'GET' | 'POST' | 'PUT' | 'DELETE';
          campoParametroId?: string; // campo cujo valor preenche {valor} na URL
          formato?: 'numero-empresa' | 'direto'; // como parsear o campo de parâmetro
        };
        acaoSenao?: {
          tipo: 'chamar_api' | 'nao_chamar';
          url: string;
          metodo: 'GET' | 'POST' | 'PUT' | 'DELETE';
          campoParametroId?: string;
          formato?: 'numero-empresa' | 'direto';
        };
      }>;
    };
  }>;
  // Mantém compatibilidade com versão anterior
  tipo: 'mover_card' | 'enviar_email' | 'enviar_whatsapp' | 'multipla' | 'exibir_ocultar' | 'atribuir_api';
  createdAt: Date;
  updatedAt: Date;
  status: 'ativo' | 'inativo';
  empresaId: string;
  // ============================================================
  // Gatilho da automação
  // - 'etapa' (default/legado): dispara quando um card entra na etapaOrigemId
  // - 'agendado': dispara em horário agendado (cron simples)
  // ============================================================
  tipoGatilho?: 'etapa' | 'agendado';
  agendamento?: {
    frequencia: 'diario' | 'mensal';
    diaDoMes?: number;  // 1..31 (usado quando frequencia === 'mensal')
    hora: number;       // 0..23
    minuto: number;     // 0..59
    ultimaExecucao?: string; // 'YYYY-MM-DD HH:mm' última vez que rodou (evita re-executar)
  };
}

// User Management and Permissions
export type UserRole = 'administrator' | 'editor' | 'viewer';

// Na interface User, adicione a propriedade empresasPermitidas
export interface MenuPermissao {
  modulo: string;
  nivel: 'visualizar' | 'editar';
  fluxosIds?: string[]; // vazio/undefined = todos os fluxos
}

export interface User {
  id: string;
  nome: string;
  email: string;
  senha: string;
  role: UserRole;
  foto?: string; // URL da foto do perfil
  empresaId?: string;
  empresasPermitidas?: string[]; // Adicione esta linha para permitir a lista de empresas acessíveis
  departamentoId?: string;
  status: 'pendente' | 'ativo' | 'inativo';
  verificadoEm?: Date;
  createdAt: Date;
  updatedAt: Date;
  permissoes?: {
    departamentos?: string[];
    flows?: string[];
    procedimentos?: string[];
    modulos?: ('flows' | 'departamentos' | 'colaboradores' | 'procedimentos' | 'organograma' | 'automacoes')[];
    menus?: MenuPermissao[];
  };
}

export interface Convite {
  id: string;
  email: string;
  role: UserRole;
  empresaId: string;
  departamentoId?: string;
  status: 'pendente' | 'aceito' | 'expirado';
  codigoVerificacao: string;
  criadoPor: string;
  createdAt: Date;
  expiresAt: Date;
}

// Interface para o login
export interface LoginCredentials {
  email: string;
  senha: string;
  empresaId?: string;
}

// Interface para a sessão
export interface UserSession {
  usuario: User;
  empresa?: Empresa;
  token?: string;
}

// Interface para o modal de procedimento
export interface ProcedimentoModalProps {
  procedimento: Procedimento | null;
  departamentos: Departamento[];
  colaboradores: Colaborador[];
  onSave: (procedimento: Omit<Procedimento, 'id' | 'createdAt' | 'updatedAt' | 'diagramaProcesso'> | Procedimento) => void;
  onClose: () => void;
}

// Email Template interfaces
export interface EmailTemplate {
  id: number;
  nome: string;
  assunto: string;
  conteudo: string;
  destinatarios: string[];
  cc: string[];
  bcc: string[];
  campos_utilizados: string[];
  ativo: boolean;
  created_at: string;
  updated_at: string;
  empresaId?: string;
}

// Permission checking functions
export function checkEditorFlowPermissions(
  userRole: string,
  currentUserId: string,
  flowCreatorId: string,
  actionType: 'create' | 'edit' | 'delete' | 'view' | 'addField' | 'editField' | 'deleteField' | 'addData'
): boolean {
  // Administradores podem fazer tudo
  if (userRole === 'administrator') return true;
  
  // Usuários consulta só podem visualizar e inserir dados (se participantes)
  if (userRole === 'viewer') {
    return actionType === 'view' || actionType === 'addData';
  }
  
  // Editores têm permissões específicas
  if (userRole === 'editor') {
    const isFlowCreator = flowCreatorId === currentUserId;
    
    switch (actionType) {
      case 'create':
      case 'view':
        return true; // Editores podem criar novos fluxos e visualizar todos
      case 'addData':
        // Editores podem adicionar dados (cards) apenas em fluxos que criaram
        // Para etapas específicas, a verificação é feita em checkCardEditPermission
        return isFlowCreator;
      case 'edit':
      case 'addField':
      case 'editField':
        // ✅ CORRIGIDO: Editores só podem editar fluxo completo se forem criadores
        return isFlowCreator;
      case 'delete':
      case 'deleteField':
        return isFlowCreator; // Só pode excluir fluxos/campos que criou      
      default:
        return false;
    }
  }
  
  return false;
}

export function checkEditorFieldPermissions(
  userRole: string,
  currentUserId: string,
  flowCreatorId: string,
  actionType: 'create' | 'edit' | 'delete' | 'view' | 'addData',
  fieldCreatorId?: string
): boolean {
  // Administradores podem fazer tudo
  if (userRole === 'administrator') return true;
  
  // Usuários consulta só podem visualizar e inserir dados
  if (userRole === 'viewer') {
    return actionType === 'view' || actionType === 'addData';
  }
  
  // Editores têm permissões específicas
  if (userRole === 'editor') {
    const isFlowCreator = flowCreatorId === currentUserId;
    const isFieldCreator = fieldCreatorId === currentUserId;
    
    switch (actionType) {
      case 'create':
      case 'view':
      case 'addData':
        return isFlowCreator; // Só pode criar campos em fluxos que criou      
      case 'edit':
        return isFlowCreator && isFieldCreator; // Só pode editar campos que criou em fluxos que criou      
      case 'delete':
        return isFlowCreator && isFieldCreator; // Só pode excluir campos que criou em fluxos que criou      
      default:
        return false;
    }
  }
  
  return false;
}

export function checkUserManagementPermissions(
  userRole: string,
  actionType: 'create' | 'edit' | 'delete' | 'changePassword' | 'view'
): boolean {
  // Administradores podem gerenciar usuários
  if (['administrator', 'admin', 'company_admin', 'global_admin'].includes(userRole)) return true;
  
  // Editores e viewers não podem criar usuários ou alterar senhas
  if (actionType === 'create' || actionType === 'changePassword') {
    return false;
  }
  
  // Todos podem visualizar
  if (actionType === 'view') return true;
  
  return false;
}

// ========== PORTAIS - Comunicação Externa ==========
// Configuração de um campo no formulário do portal
export interface PortalCampoConfig {
  id: string;
  campoOriginalId?: string; // ID do campo original do fluxo (se vinculado)
  nome: string;
  tipo: 'texto' | 'texto_longo' | 'data' | 'lista' | 'checkbox' | 'checkbox_single' | 'checkbox_multi' | 'valor' | 'anexo' | 'email' | 'telefone' | 'cpf_cnpj' | 'justificativa_checkbox' | 'documento' | 'texto_informativo';
  documentoTemplateId?: string;
  obrigatorio: boolean;
  /** Campos de anexo: quantidade mínima de arquivos exigida (0/undefined = usa `obrigatorio`) */
  minAnexos?: number;
  /** Campos de anexo: quantidade máxima de arquivos aceita (default 6) */
  maxAnexos?: number;
  opcoes?: string[]; // Para campos de lista/checkbox
  placeholder?: string;
  descricao?: string; // Texto de ajuda para o usuário externo
  mascara?: string; // Máscara de input (ex: telefone, CPF)
  ordem: number;
  // Visibilidade para o externo após submissão
  visivelAposEnvio?: boolean; // Se o campo aparece na área de acompanhamento
  editavelAposEnvio?: boolean; // Se pode editar depois de enviar
  // Condicionais
  campoVinculadoId?: string; // ID do campo checkbox vinculado (para justificativa_checkbox ou exibição condicional)
  valorCondicional?: string; // Valor que o campoVinculado deve ter para mostrar este campo
  // Texto Informativo (Label)
  textoInformativo?: string;
  ocultarTitulo?: boolean;
  formatacao?: {
    negrito?: boolean;
    italico?: boolean;
    tamanho?: 'pequeno' | 'medio' | 'grande';
    cor?: string;
    alinhamento?: 'esquerda' | 'centro' | 'direita';
  };
}

// Configuração de aparência do Portal
export interface PortalAparencia {
  // Cores
  corPrimaria: string;
  corSecundaria?: string;
  corTexto?: string;
  corTextoSecundario?: string;
  corFundo?: string;
  
  // Fundo
  tipoFundo?: 'cor' | 'gradiente' | 'imagem';
  gradienteCor1?: string;
  gradienteCor2?: string;
  gradienteAngulo?: number;
  imagemFundo?: string; // URL da imagem
  imagemFundoOpacidade?: number; // 0-100
  
  // Imagens
  logo?: string;
  logoAltura?: number; // px
  imagemBanner?: string;
  bannerAltura?: number; // px
  favicon?: string;
  
  // Layout
  larguraMaxima?: number; // px (default 640)
  borderRadius?: string; // px
  sombraCards?: boolean;
  
  // Tipografia
  fonteFamilia?: string;
  fonteCorpo?: number; // px
  fonteTitulo?: number; // px
  
  // Estilo do Header
  estiloHeader?: 'solido' | 'gradiente' | 'transparente';
  
  // Estilo dos Cards
  estiloCards?: 'classico' | 'glass' | 'flat' | 'elevado';
  
  // Hero da Landing Page
  estiloHero?: 'simples' | 'banner' | 'split';
}

// Funcionalidades do Portal
export interface PortalFuncionalidades {
  permitirChat: boolean;
  permitirAcompanhamento: boolean;
  gerarProtocolo: boolean;
  exibirEtapaAtual: boolean;
  exibirHistorico: boolean;
  permitirAnexos: boolean;
  limitarTamanhoAnexo?: number; // MB
  extensoesPermitidas?: string[];
  permitirEdicaoAposEnvio?: boolean;
  mostrarCamposAtualizados?: boolean;
  mostrarCampoTitulo?: boolean; // Se true, exibe campo para digitar título do cartão
  acessoPorCodigo?: boolean; // Se true, "minhas solicitações" exige código enviado ao e-mail
}

// Configuração de notificações do Portal
export interface PortalNotificacoes {
  // Para o externo (fornecedor/cliente)
  enviarEmailConfirmacao: boolean;
  emailConfirmacaoAssunto?: string;
  emailConfirmacaoMensagem?: string;
  templateEmailConfirmacao?: string;
  
  enviarEmailAtualizacao: boolean; // Quando campos são atualizados internamente
  emailAtualizacaoAssunto?: string;
  templateEmailAtualizacao?: string;
  
  emailRespostaMensagem?: boolean; // Quando equipe responde mensagem
  
  // Para a equipe interna
  notificarNovaSubmissao?: boolean;
  notificarNovaMensagem?: boolean;
  emailsInternosNotificacao: string[]; // Lista de emails para notificar
  whatsappNotificacao?: string; // Número para notificar
}

// Configuração do Portal
export interface Portal {
  id: string;
  flowId: string;
  empresaId: string;
  nome: string;
  descricao?: string;
  
  // Configuração do formulário
  campos: PortalCampoConfig[];
  
  // Configuração de destino
  etapaDestinoId: string;
  criarNovoCard: boolean;
  
  // Aparência completa
  aparencia?: PortalAparencia;
  
  // Aparência legada (compatibilidade)
  corPrimaria?: string;
  corSecundaria?: string;
  logo?: string;
  
  // Textos personalizáveis
  textos?: {
    tituloPagina?: string; // <title> da página
    mensagemInicial?: string;
    mensagemSucesso?: string;
    botaoEnviar?: string;
    tituloPaginaAcompanhamento?: string;
    mensagemAcompanhamento?: string;
  };
  
  // Textos legados (compatibilidade)
  mensagemInicial?: string;
  mensagemSucesso?: string;
  
  // Funcionalidades extras
  funcionalidades?: PortalFuncionalidades;
  
  // Notificações
  notificacoes?: PortalNotificacoes;
  
  // Segurança
  ativo: boolean;
  expiracaoLink?: Date;
  limitarSubmissoes?: number;
  requererAutenticacao?: boolean;
  senhaAcesso?: string; // Senha opcional para acessar
  
  // Tokens
  tokenPublico: string;
  
  // URL Pública (para links externos)
  urlPublica?: string; // Ex: https://portal.suaempresa.com
  
  // Estatísticas
  totalSubmissoes: number;
  totalMensagens?: number;
  
  // Controle
  criadoPor: string;
  createdAt: Date;
  updatedAt: Date;
}

// Submissão feita através do portal
export interface PortalSubmissao {
  id: string;
  portalId: string;
  flowId: string;
  empresaId: string;
  
  // Dados preenchidos
  dados: Record<string, any>;
  
  // Identificação do externo
  nomeExterno?: string;
  emailExterno?: string;
  telefoneExterno?: string;
  
  // Protocolo gerado
  protocolo?: string;
  
  // Rastreamento
  cardCriadoId?: string;
  etapaId: string;
  etapaAtualNome?: string; // Para mostrar ao externo
  
  // Token de acesso (para o fornecedor voltar a acessar)
  tokenAcesso: string;
  
  // Metadados
  ip?: string;
  userAgent?: string;
  origem?: string;
  
  // Status
  status: 'pendente' | 'processado' | 'erro' | 'arquivado';
  mensagemErro?: string;
  
  // Interações
  totalMensagens: number;
  mensagensNaoLidas: number; // Para a equipe interna
  ultimaInteracao?: Date;
  
  createdAt: Date;
  updatedAt: Date;
}

// Mensagem do chat do portal
export interface PortalMensagem {
  id: string;
  submissaoId: string;
  portalId: string;
  
  // Autor
  origem: 'externo' | 'interno'; // Quem enviou
  remetenteId?: string; // ID do colaborador se interno
  remetenteNome?: string;
  remetenteAvatar?: string;
  
  // Conteúdo
  mensagem: string;
  anexos?: {
    nome: string;
    url: string;
    tipo: string;
    tamanho: number;
  }[];
  
  // Status
  lida: boolean;
  lidaEm?: Date;
  lidaPor?: string;
  
  createdAt: string;
}

// WhatsApp Template interfaces
export interface WhatsAppTemplate {
  _id?: string; // MongoDB id
  id?: string;  // optional client-side id
  nome: string;
  conteudo: string; // plain text with {{variaveis}}
  destinatarios?: string[]; // E.164 numbers like +5511999999999
  campos_utilizados?: string[];
  midia?: {
    tipo: 'image' | 'document';
    url?: string; // uploaded URL if stored externally
    dataUrl?: string; // inline base64 if needed
    nome?: string;
  } | null;
  ativo: boolean;
  created_at: string;
  updated_at: string;
  empresaId?: string;
}

// ========== MÓDULO DE TAREFAS ==========
export type TaskPriority = 'baixa' | 'normal' | 'alta' | 'urgente';
export type TaskStatus = 'pendente' | 'em_andamento' | 'aguardando' | 'concluida' | 'cancelada';

export interface TaskEscalonamento {
  nivel: number; // 1 = primeiro escalonamento (superior direto), 2 = segundo (superior do superior), etc
  responsavelId: string; // ID do colaborador para quem foi escalonado (preenchido automaticamente via organograma)
  escalonadoEm: Date;
  motivoAutomatico: string; // Ex: "Prazo excedido em 24h"
}

export interface TaskHistorico {
  id: string;
  acao: 'criada' | 'atribuida' | 'status_alterado' | 'prazo_alterado' | 'escalonada' | 'comentario' | 'editada' | 'concluida' | 'cancelada' | 'justificativa';
  descricao: string;
  realizadoPor: string; // ID do colaborador ou 'sistema' para ações automáticas
  realizadoEm: Date;
  dadosAnteriores?: Record<string, any>;
  dadosNovos?: Record<string, any>;
}

export interface TaskComentario {
  id: string;
  texto: string;
  autorId: string;
  criadoEm: Date;
  editadoEm?: Date;
}

export interface TaskAnexo {
  id: string;
  nome: string;
  nomeOriginal: string;
  tipo: string; // mime type
  tamanho: number; // em bytes
  caminho: string; // caminho relativo no servidor
  enviadoPor: string; // ID do colaborador
  enviadoEm: Date;
}

export interface Task {
  id: string;
  titulo: string;
  descricao?: string;
  
  // Responsabilidade
  solicitanteId: string; // Quem criou/solicitou a tarefa
  responsavelId: string; // Quem deve executar
  observadoresIds?: string[]; // Quem acompanha (recebe notificações)
  
  // Datas e Prazo
  criadaEm: Date;
  atualizadaEm: Date;
  prazo: Date;
  concluidaEm?: Date;
  
  // Status e Prioridade
  status: TaskStatus;
  prioridade: TaskPriority;
  
  // Escalonamento automático (baseado no organograma)
  // O sistema escala automaticamente para o superior do responsável após prazo vencido
  escalonamentos?: TaskEscalonamento[]; // Histórico de escalonamentos realizados
  nivelEscalonamentoAtual: number; // 0 = sem escalonamento, 1 = superior direto, 2 = superior do superior, etc
  
  // Justificativa obrigatória quando tarefa está atrasada
  justificativaAtraso?: string;
  justificativaEm?: Date;
  justificativaPor?: string;
  
  // Histórico e Comentários
  historico: TaskHistorico[];
  comentarios?: TaskComentario[];
  
  // Anexos (arquivos salvos no servidor)
  anexos?: TaskAnexo[];
  
  // Vínculo com outros módulos (opcional)
  flowId?: string;
  cardId?: string;
  
  // Empresa
  empresaId: string;
  
  // Tags/Categorias
  tags?: string[];

  // Etapa/Coluna customizada (para o configurador de projetos)
  etapaId?: string;

  // Personalização visual do card
  cor?: string; // Cor de destaque do card (hex)
  icone?: string; // Emoji ou ícone do card
  capa?: string; // URL de imagem de capa
  isProjeto?: boolean; // Se true, card funciona como um projeto

  // Campos personalizados (configuração e valores)
  camposConfig?: TaskCampoConfig[]; // Definição dos campos
  camposValores?: Record<string, any>; // Valores dos campos { campoId: valor }
}

// Item da lista de progresso
export interface ItemProgresso {
  id: string;
  nome: string;
  concluido: boolean;
  concluidoEm?: Date;
  concluidoPor?: string;
  desconcluidoEm?: Date;
  desconcluidoPor?: string;
  comentario?: string;
  ordem: number;
}

// Campo personalizado para tarefas
export interface TaskCampoConfig {
  id: string;
  nome: string;
  tipo: 'texto' | 'texto_longo' | 'data' | 'lista' | 'checkbox' | 'checkbox_multi' | 'numero' | 'valor' | 'telefone' | 'email' | 'link' | 'lista_progresso';
  obrigatorio: boolean;
  opcoes?: string[]; // Para lista e checkbox_multi
  placeholder?: string;
  valorPadrao?: any;
}

// ========== CONFIGURADOR DE ETAPAS/COLUNAS DE TAREFAS ==========
export interface TaskEtapa {
  id: string;
  nome: string;
  descricao?: string;
  cor: string; // Cor da coluna (hex)
  icone?: string; // Emoji ou nome do ícone
  ordem: number;
  
  // Tipo da etapa
  tipo: 'padrao' | 'inicial' | 'final' | 'atrasada'; // atrasada = calculada automaticamente
  
  // Configurações
  limiteTarefas?: number; // WIP limit
  autoMoverParaAtrasadas?: boolean; // Se true, tarefas nesta etapa vão para "atrasadas" quando vencem
  
  // Permissões
  responsaveisIds?: string[]; // IDs dos colaboradores que podem ter tarefas nesta etapa
  
  // Metadados
  criadoEm: Date;
  atualizadoEm: Date;
}

export interface TaskProjeto {
  id: string;
  nome: string;
  descricao?: string;
  cor: string;
  icone?: string;
  
  // Etapas customizadas do projeto (se vazio, usa as padrão)
  etapas: TaskEtapa[];
  
  // Configurações
  ativo: boolean;
  empresaId: string;
  
  // Metadados
  criadoEm: Date;
  atualizadoEm: Date;
  criadoPor: string;
}